/**
 * Bucketing the comparable pool into the benchmark the agent argues from.
 *
 * TWO SERIES THAT MUST NOT BE AVERAGED TOGETHER
 *
 * An asking price is what someone hopes for. A sold price is what someone
 * paid. They are different quantities, and the gap between them is often the
 * most useful thing on the page — it is the discount the market is actually
 * taking. Averaging them produces a number that is neither, so active
 * listings and closed sales are bucketed and drawn separately, always.
 *
 * WHY A BOX AND NOT A CANDLE
 *
 * A candlestick encodes open/high/low/close, which are properties of a traded
 * instrument with a sequence of prices inside each period. A set of listings
 * has no open and no close — there is no first or last trade, only a spread.
 * So each period is drawn as a quartile box: the middle half as the body, the
 * full range as the whisker, the median as the line. It reads almost exactly
 * like a candle and it does not claim four numbers that do not exist.
 */
import type { SubjectComparable } from '@/types/marketPricing'

/** The window the user asked for on closed sales: roughly 18 months. */
export const CLOSED_WINDOW_DAYS = 545

/**
 * Below this, a box is a shape drawn over noise.
 *
 * Three observations have a median and quartiles in the arithmetic sense and
 * none in the useful sense. Periods under the floor are still shown — as
 * their individual points, so the agent can see the evidence is thin rather
 * than seeing nothing and assuming no market.
 */
export const MIN_FOR_BOX = 4

export type SeriesKind = 'active' | 'closed'

export interface Bucket {
  /** Period key, e.g. "2026-Q2". */
  key: string
  year: number
  quarter: number
  kind: SeriesKind
  count: number
  low: number
  p25: number
  median: number
  p75: number
  high: number
  /** Raw values, so a thin period can be drawn as points. */
  values: number[]
  /** False when `count < MIN_FOR_BOX`: show points, not a box. */
  drawBox: boolean
}

function quantile(sorted: number[], q: number): number {
  if (sorted.length === 1) return sorted[0]
  const position = (sorted.length - 1) * q
  const lower = Math.floor(position)
  const upper = Math.ceil(position)
  if (lower === upper) return sorted[lower]
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (position - lower)
}

export function quarterKeyOf(date: Date): { key: string; year: number; quarter: number } {
  const year = date.getUTCFullYear()
  const quarter = Math.floor(date.getUTCMonth() / 3) + 1
  return { key: `${year}-Q${quarter}`, year, quarter }
}

/**
 * Group comparables into quarterly buckets, one set per series.
 *
 * Closed sales are limited to `CLOSED_WINDOW_DAYS`; active listings are not,
 * because an active listing is by definition current regardless of when it
 * was first seen. A comparable with no usable date or price is dropped rather
 * than placed in an arbitrary period — a guessed date would put real money in
 * the wrong quarter.
 */
export function bucketComparables(
  comparables: SubjectComparable[],
  now: number = Date.now(),
): Bucket[] {
  const groups = new Map<string, { year: number; quarter: number; kind: SeriesKind; values: number[] }>()

  for (const comparable of comparables) {
    const price = Number(comparable.normalized_price)
    if (!Number.isFinite(price) || price <= 0) continue

    const raw = comparable.observed_at
    if (!raw) continue
    const date = new Date(raw)
    if (Number.isNaN(date.getTime())) continue

    const kind: SeriesKind = comparable.is_closed ? 'closed' : 'active'
    const ageDays = (now - date.getTime()) / 86_400_000
    if (kind === 'closed' && ageDays > CLOSED_WINDOW_DAYS) continue
    // A date in the future is bad data, not a forecast.
    if (ageDays < -1) continue

    const { key, year, quarter } = quarterKeyOf(date)
    const id = `${kind}:${key}`
    const existing = groups.get(id)
    if (existing) existing.values.push(price)
    else groups.set(id, { year, quarter, kind, values: [price] })
  }

  return Array.from(groups.entries())
    .map(([id, group]) => {
      const sorted = [...group.values].sort((a, b) => a - b)
      return {
        key: id.split(':')[1],
        year: group.year,
        quarter: group.quarter,
        kind: group.kind,
        count: sorted.length,
        low: sorted[0],
        p25: quantile(sorted, 0.25),
        median: quantile(sorted, 0.5),
        p75: quantile(sorted, 0.75),
        high: sorted[sorted.length - 1],
        values: sorted,
        drawBox: sorted.length >= MIN_FOR_BOX,
      }
    })
    .sort((a, b) => a.year - b.year || a.quarter - b.quarter || a.kind.localeCompare(b.kind))
}

/** Distinct periods present, in order — the x axis of the benchmark chart. */
export function periodsOf(buckets: Bucket[]): { key: string; year: number; quarter: number }[] {
  const seen = new Map<string, { key: string; year: number; quarter: number }>()
  for (const bucket of buckets) {
    if (!seen.has(bucket.key)) {
      seen.set(bucket.key, { key: bucket.key, year: bucket.year, quarter: bucket.quarter })
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.year - b.year || a.quarter - b.quarter)
}

/**
 * The spread between what sellers ask and what buyers pay, where both exist.
 *
 * Null unless the same period has both series with enough observations to
 * have a meaningful median. This is the single most quotable number on the
 * page when it exists, and the most misleading if computed across different
 * periods or from two listings.
 */
export function askToSoldGap(buckets: Bucket[]): { percent: number; period: string } | null {
  const periods = periodsOf(buckets)
  for (let index = periods.length - 1; index >= 0; index -= 1) {
    const period = periods[index].key
    const active = buckets.find((b) => b.key === period && b.kind === 'active' && b.drawBox)
    const closed = buckets.find((b) => b.key === period && b.kind === 'closed' && b.drawBox)
    if (active && closed && active.median > 0) {
      return {
        percent: Number((((closed.median - active.median) / active.median) * 100).toFixed(1)),
        period,
      }
    }
  }
  return null
}
